import { useMemo } from 'react';

import { useWorkspaceResourceCollections } from '@/hooks/useWorkspaceResourceCollections';
import {
    useLifecycleList,
    useRelationsSearchMetadataQuery,
    useSearchMetadataQuery,
    useWorkspaceSearchMetadataQuery,
} from '@/queries';
import { hasFeatureAddOnAccess } from '@/shared/entitlements';
import { useBillingStore } from '@/stores/database/aliases/billing';

import { ECONOMIC_SEARCH_CATALOG, SearchTabKey } from '../../searchTabs';

export interface SearchTabCount {
    complete?: boolean;
    loading: boolean;
    unit: string;
    value?: number;
}

export type SearchTabCounts = Partial<Record<SearchTabKey, SearchTabCount>>;

/** Resolves only corpus-level totals; bounded provider result pages are deliberately excluded. */
export const useSearchTabCounts = (): SearchTabCounts => {
    const {
        activeWorkspaceId,
        counts,
        failedResourceTypes,
        loading: workspaceCollectionsLoading,
        readableResourceTypes,
    } = useWorkspaceResourceCollections();
    const billingSnapshot = useBillingStore((state) => state.snapshot);
    const lifecyclesEnabled = hasFeatureAddOnAccess(billingSnapshot, 'lifecycles');
    const lifecycleListQuery = useLifecycleList({ enabled: lifecyclesEnabled });
    const lifecycleCount = lifecycleListQuery.data?.items.length ?? 0;
    const searchMetadata = useSearchMetadataQuery();
    const relationsMetadata = useRelationsSearchMetadataQuery(activeWorkspaceId);
    const relationsPeopleCount = relationsMetadata.data?.peopleCount;
    const workspaceSearchMetadata = useWorkspaceSearchMetadataQuery(activeWorkspaceId);
    const workspaceResourceCount = readableResourceTypes.reduce(
        (total, resourceType) => total + counts[resourceType],
        0,
    );

    return useMemo(() => {
        const workspaceReady =
            Boolean(activeWorkspaceId) &&
            !workspaceCollectionsLoading &&
            (!lifecyclesEnabled || !lifecycleListQuery.isPending);
        const workspaceFailed = Boolean(
            failedResourceTypes.length || (lifecyclesEnabled && lifecycleListQuery.isError),
        );
        const companyCount = searchMetadata.data?.counts.companies?.count;
        const connectedDocuments = workspaceSearchMetadata.data?.counts.documents;
        const filingCount = searchMetadata.data?.counts.filings?.count;
        const documentCountReady = connectedDocuments !== undefined && filingCount !== undefined;

        return {
            companies: {
                loading: searchMetadata.isPending,
                unit: 'companies',
                value: companyCount,
            },
            economic: {
                loading: false,
                unit: 'economic series',
                value: ECONOMIC_SEARCH_CATALOG.length,
            },
            documents: {
                complete: connectedDocuments?.complete,
                loading: workspaceSearchMetadata.isPending || searchMetadata.isPending,
                unit: 'searchable documents and filings',
                value: documentCountReady ? connectedDocuments.count + filingCount : undefined,
            },
            people: {
                loading:
                    relationsMetadata.isPending ||
                    Boolean(
                        relationsPeopleCount &&
                        !relationsPeopleCount.complete &&
                        relationsPeopleCount.status !== 'error',
                    ),
                unit: 'searchable people',
                value: relationsPeopleCount?.complete ? relationsMetadata.data?.peopleRecords : undefined,
            },
            workspace: {
                loading: !workspaceReady,
                unit: 'workspace resources',
                value:
                    workspaceReady && !workspaceFailed
                        ? workspaceResourceCount + lifecycleCount
                        : undefined,
            },
        };
    }, [
        activeWorkspaceId,
        failedResourceTypes.length,
        lifecycleCount,
        lifecycleListQuery.isError,
        lifecycleListQuery.isPending,
        lifecyclesEnabled,
        relationsMetadata.data?.peopleRecords,
        relationsMetadata.isPending,
        relationsPeopleCount,
        searchMetadata.data?.counts.companies?.count,
        searchMetadata.data?.counts.filings?.count,
        searchMetadata.isPending,
        workspaceCollectionsLoading,
        workspaceResourceCount,
        workspaceSearchMetadata.data?.counts.documents,
        workspaceSearchMetadata.isPending,
    ]);
};
